import { useState, useEffect } from "react";
import { Loader2, Edit2 } from "lucide-react";
import { Button } from "../components/ui/button";
import PageEditor from "../components/PageEditor";

const API = "/api";

export default function About({ user }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    fetchPage();
  }, []);

  const fetchPage = async () => {
    try {
      const response = await fetch(`${API}/pages/about`);
      if (response.ok) {
        const data = await response.json();
        setPage(data);
      } else {
        setError("Failed to load page");
      }
    } catch (err) {
      setError("Failed to load page");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = (newContent) => {
    setPage(prev => ({ ...prev, content: newContent, updated_at: new Date().toISOString() }));
    setEditMode(false);
  };

  const isMasterAdmin = user?.role === "master_admin";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-card rounded-2xl p-8 shadow-lg border border-border overflow-hidden">
          {editMode ? (
            <PageEditor
              pageSlug="about"
              initialContent={page?.content || ""}
              title="About"
              onSave={handleSave}
              onCancel={() => setEditMode(false)}
            />
          ) : (
            <>
              {/* Header with Edit Button */}
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold text-foreground" data-testid="page-title">
                  {page?.title || "About"}
                </h1>
                {isMasterAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditMode(true)}
                    className="gap-1.5"
                    data-testid="edit-page-btn"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit Page
                  </Button>
                )}
              </div>
              
              {/* Content */}
              <div 
                className="page-content prose prose-lg max-w-none
                  prose-headings:text-foreground
                  prose-p:text-foreground/80
                  prose-li:text-foreground/80
                  prose-strong:text-foreground
                  prose-a:text-primary hover:prose-a:text-primary/80
                  prose-ul:list-disc prose-ol:list-decimal
                  prose-img:rounded-lg prose-img:shadow-lg
                  [&_iframe]:w-full [&_iframe]:max-w-[640px] [&_iframe]:h-[360px] [&_iframe]:rounded-lg"
                style={{ overflowWrap: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: page?.content || "" }}
                data-testid="page-content"
              />
              
              {/* Footer */}
              {page?.updated_at && (
                <p className="text-sm text-muted-foreground mt-8 pt-4 border-t border-border">
                  Last updated: {new Date(page.updated_at).toLocaleDateString()}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
